import { notification } from "antd";
import cuid from "cuid";
import { defaults, filter, get, keyBy, last, map, sortBy, uniq } from "lodash";
import moment from "moment";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { COLORS } from "shared/style/colors";
import { GENERATE_QUOTE_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { CREATE_LINK } from "utils/api/graphql/mutations/link";
import { SYNC_PROJECT_GUIMINI } from "utils/api/graphql/mutations/projects";
import { SEND_B2C_QUOTE } from "utils/api/graphql/mutations/quotes";
import { SUBSCRIPTION } from "utils/api/graphql/queries/subscription";
import { CONTACT_POINT_SYSTEM, GUIMINI_STEPS, KEYS } from "utils/constants";
import { buildCombinations } from "utils/helpers/combinations";
import { filterByConditions } from "../../../..";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

const AdherentInfo = ({
  processId,
  project,
  onNext,
  onBack,
  currentStatus,
  tracer,
}) => {
  const projectSubscriptions = get(project, "subscriptions", []);
  const lastActiveSubscription = get(
    projectSubscriptions
      .filter(({ status }) => status !== "CANCELED")
      .slice(-1),
    "0",
    get(projectSubscriptions, "0"),
  );
  const subscriptionId =
    localStorage.getItem(KEYS.SUBSCRIPTION_ID) ||
    get(project, "fields.currentSubscription") ||
    get(lastActiveSubscription, "id");

  const telecoms = defaults(keyBy(get(project, "contact.telecoms"), "system"), {
    [CONTACT_POINT_SYSTEM.PHONE]: { value: null },
    [CONTACT_POINT_SYSTEM.EMAIL]: { value: null },
  });
  const [generateQuoteDocuments] = useMutation(GENERATE_QUOTE_DOCUMENTS);
  const [addLink] = useMutation(CREATE_LINK);
  const [sendQuoteMutation] = useMutation(SEND_B2C_QUOTE);
  const [syncProjectWithGuimini] = useMutation(SYNC_PROJECT_GUIMINI);

  const { data } = useQuery(SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
  });

  const onSubmit = ({ telecoms, ...contact }) => {
    const status = get(
      last(
        sortBy(
          filter(
            project.statuses,
            ({ status }) => status.process.id === processId,
          ),
          "createdDate",
        ),
      ),
      "status",
    );
    const { possibleStatuses } = status;
    const [possibleStatus] = filterByConditions(
      sortBy(possibleStatuses, "order"),
      {
        ...project,
        status,
      },
    );
    const updatedTelecoms = map(telecoms, (item, key) =>
      defaults(item, { id: cuid(), system: key }),
    );
    const baseInsurancePlanId = get(data, "subscription.insurancePlan.id");
    const upsellsByBase = get(project, "fields.upsells", {});
    const persistedCombinations = get(project, "fields.combinations", []);
    const combinations =
      persistedCombinations.length > 0
        ? persistedCombinations
        : baseInsurancePlanId
          ? buildCombinations({
              bases: [
                {
                  insurancePlan: { id: baseInsurancePlanId },
                  cost: 0,
                },
              ],
              upsellsByBase,
            }).map((combination) => ({
              id: combination.id,
              baseInsurancePlanId: get(combination, "baseInsurancePlan.id"),
              complementaryInsurancePlanId:
                get(combination, "complementaryInsurancePlan.id") || null,
            }))
          : [];
    const baseInsurancePlanIds = uniq(
      combinations.map((combination) => combination.baseInsurancePlanId),
    );
    onNext({
      payload: {
        contact: {
          ...contact,
          id: get(project, "contact.id") || cuid(),
          telecoms: updatedTelecoms,
          user: {
            id: cuid(),
            birthDate: moment("11/11/1993"),
            ...contact.user,
          },
        },
        terminationRequest: {
          id: cuid(),
          ...get(project, "terminationRequest"),
          brokerageMandate: {
            id: cuid(),
            ...get(project, "terminationRequest.brokerageMandate"),
            firstName: get(contact, "user.firstname"),
            lastName: get(contact, "user.lastname"),
          },
        },
        fields: {
          ...get(project, "fields", {}),
          combinations,
        },
        visible: true,
      },
      onCompleted: () => {
        generateQuoteDocuments({
          variables: {
            data: {
              project: { id: project.id },
              insurancePlans: baseInsurancePlanIds.map((id) => ({ id })),
            },
          },
          onCompleted: () => {
            const source = localStorage.getItem(KEYS.SOURCE);
            addLink({
              variables: {
                data: {
                  url: get(
                    data,
                    "subscription.insurancePlan.config.generatedUrl",
                    `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
                  ),
                  project: { id: project.id },
                  subscription: { id: subscriptionId },
                  fields: {
                    process: { id: processId },
                    status: { id: get(possibleStatus, "nextStatus") },
                  },
                },
              },
              onCompleted: ({ addLink }) => {
                syncProjectWithGuimini({
                  variables: {
                    project: { id: project.id },
                    status: GUIMINI_STEPS.QUALIFICATION,
                  },
                });
                sendQuoteMutation({
                  variables: {
                    data: {
                      link: addLink?.id,
                      project: { id: project.id },
                      insurancePlans: baseInsurancePlanIds.map((id) => ({
                        id,
                      })),
                    },
                  },
                  onCompleted: () =>
                    notification.open({
                      message: "Devis envoyé",
                      duration: 5,
                      icon: (
                        <IoCheckmarkCircleOutline color={COLORS.C_SUCCESS} />
                      ),
                    }),
                });
              },
            });
          },
        });
      },
    });
  };
  return (
    <>
      <Navbar
        project={project}
        progress={currentStatus?.progress}
        tracer={tracer}
        step={4}
        stepCount={5}
        title="Mes informations contractuelles"
        showCart
      />
      <View
        contact={{ ...get(project, "contact", {}), telecoms }}
        onNext={onSubmit}
        onBack={onBack}
        locked={false}
      />
    </>
  );
};

export default AdherentInfo;
